import { WSList } from "@wingspanhq/fe-component-library";
import { IChannelNotificationResponse } from "@wingspanhq/notifications/dist/lib/interfaces";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useUserId } from "../../../../query/hooks/helpers";
import { useViewNotification } from "../../../../query/notifications/mutations/useViewNotification";
import { useMemberProfile } from "../../../../query/users/queries";
import { EmptyState } from "../../../../shared/components/EmptyState";
import { selectorHasPayerContextUser } from "../../../../shared/selectors/selectorHasPayerContextUser";
import { selectorIsEnterpriseUser } from "../../../../shared/selectors/selectorIsEnterpriseUser";
import { openInNewTab } from "../../../../shared/utils/openInNewTab";
import { selectorGroupedTodos } from "../../selectors/selectorGroupedTodos";
import { useTrackClickNotification } from "../../utils/useTrackClickNotification";
import { NotificationProps, NotificationTemplates } from "../Notifications";
import { Promotions, useShowPromotions } from "../Promotions";
import { TodoItem } from "../TodoItem";

type Props = {
  notifications: IChannelNotificationResponse[];
};

export const TodoList: React.FC<Props> = props => {
  const userId = useUserId();
  const queryMember = useMemberProfile(userId);
  const history = useHistory();

  const isPayerView =
    queryMember.data &&
    (selectorIsEnterpriseUser(queryMember.data.user) ||
      selectorHasPayerContextUser(queryMember.data.user));

  const todoGroups = useMemo(
    () => selectorGroupedTodos(props.notifications),
    [props.notifications]
  );

  const trackClickNotification = useTrackClickNotification(userId);
  const [readNotification] = useViewNotification();

  const showPromotions = useShowPromotions();

  return (
    <WSList>
      {todoGroups.length === 0 && !showPromotions ? (
        <EmptyState
          title={
            isPayerView
              ? `You’re all set for now.`
              : `You’re ready to get paid.`
          }
          description="We’ll add tasks here as they arise."
        />
      ) : (
        <>
          {todoGroups.map(({ group, ...notification }) => {
            const taskPreset = notification.handle
              ? NotificationTemplates[
                  notification.handle as keyof typeof NotificationTemplates
                ]
              : undefined;

            const Button = taskPreset?.Footer;
            const Header = taskPreset?.Header;
            const taskProps = {
              ...notification,
              group
            } as NotificationProps<never>;
            const hasGroups = group.length > 1;

            return (
              <TodoItem
                key={notification.channelMessageId}
                isPending={taskPreset?.isPending}
                onClick={() => {
                  if (group.length === 1) {
                    trackClickNotification(notification);
                  }
                }}
                viewed={group.every(data => data.isViewed)}
                body={
                  taskPreset
                    ? taskPreset.renderBody({
                        ...taskProps,
                        history
                      })
                    : notification.message
                }
                buttonAction={
                  taskPreset
                    ? taskPreset.buttonAction?.(taskProps, history)
                    : notification.actionURL
                    ? () => {
                        const { hostname, pathname, search, hash } = new URL(
                          notification.actionURL!
                        );

                        if (hostname !== window.location.hostname) {
                          openInNewTab(notification.actionURL!);
                          return;
                        }

                        history.push(pathname + search + hash);
                      }
                    : undefined
                }
                buttonActionAsync={
                  taskPreset
                    ? taskPreset.buttonActionAsync?.(taskProps, history)
                    : undefined
                }
                title={
                  taskPreset
                    ? taskPreset.renderTitle(taskProps)
                    : notification.subject
                }
                status={notification.status}
                footer={Button ? <Button {...taskProps} /> : null}
                header={Header ? <Header {...taskProps} /> : null}
                buttonText={
                  taskPreset
                    ? taskPreset.buttonText?.(taskProps)
                    : notification.actionTitle
                }
                numberOfTasks={hasGroups ? group.length : undefined}
                company={
                  notification.context?.client?.logoFileId && !hasGroups
                    ? {
                        logoFileId: notification.context.client.logoFileId,
                        name: notification.context.client.companyName
                      }
                    : undefined
                }
                onRead={
                  group.some(data => !data.isViewed)
                    ? () => {
                        group.forEach(data => {
                          if (!data.isViewed) {
                            readNotification(data.channelMessageId);
                          }
                        });
                      }
                    : undefined
                }
              />
            );
          })}

          <Promotions />
        </>
      )}
    </WSList>
  );
};
